import React from "react";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import { Divider } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { baseRoutes } from "@base/routes/baseRoutes";
import { ISpecialPackage } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import TagSpecialPackageStatus from "@components/CommonStyles/TagSpecialPackageStatus";
import InsuranceBadge from "@components/CommonStyles/InsuranceBadge";
import { ECurrency, ESeller, ESpecialPackageStatus } from "@base/enums";
import CommonIcons from "@base/assets/icons";
import { getAvatarLinkFromObjectName } from "@base/helpers/common";
import { isExtraFlagOn, parseExtraInfo } from "@base/helpers/extraInfo";

interface IProps {
  specialPackage: ISpecialPackage;
}

export default function ServicePackageListItem({ specialPackage }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const extraInfo = parseExtraInfo(specialPackage.extra_info);
  // Insurance packages are fulfilled through a partner, so the selling site is not something the
  // patient should act on - the package config decides whether to name it at all.
  const isSiteInfoHidden = isExtraFlagOn(extraInfo.site_info_hidden);

  return (
    <div className="w-full bg-common-background p-3 rounded-2xl">
      <div className="flex justify-between items-center">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-black text-base font-medium font-['Inter'] line-clamp-1">{specialPackage.patient_record?.patient_name}</span>
          <span className="text-common-subtext text-xs font-normal font-['Inter'] line-clamp-1">
            {[
              specialPackage.patient_record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              specialPackage.patient_record?.patient_dob ? dayjs(specialPackage.patient_record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </div>

        {/* An insurer's package carries the verified mark in the top right corner. */}
        <InsuranceBadge specialPackage={specialPackage} />
      </div>

      <Divider className="my-4" />

      <div className="flex justify-between items-center gap-4 max-sm:flex-col max-sm:justify-start max-sm:items-start max-sm:gap-2">
        <div className="min-w-0 flex items-start gap-4">
          <div className="w-fit max-md:hidden">
            <CommonStyles.ImageUI height={125} width={222} src={specialPackage.thumbnail} fallback={CommonImages.DrafPackage} />
          </div>

          <div className="flex flex-col gap-2">
            <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-base font-bold font-['Inter'] !mb-0">
              {specialPackage.package_name}
            </CommonStyles.TypographyUI>

            {/* Insurance-paid packages do not show the price to the user. */}
            {specialPackage.seller !== ESeller.INSURANCE ? (
              <CommonStyles.TypographyUI use="paragraph" className="text-common-inprogress text-base font-medium font-['Inter'] !mb-0">
                {specialPackage.amount?.toLocaleString()}
                {ECurrency.VND}
              </CommonStyles.TypographyUI>
            ) : null}

            {!isSiteInfoHidden ? (
              <div className="flex items-center gap-1">
                <CommonStyles.ImageUI
                  width={21}
                  height={21}
                  src={getAvatarLinkFromObjectName("thumbnail", specialPackage.site?.avatar ?? [])}
                  fallback={CommonImages.location}
                />
                <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-xs font-normal font-['Inter'] !mb-0 inline-block">
                  {specialPackage.site?.name}
                  {specialPackage.site_branch_name ? ", " + specialPackage.site_branch_name : ""}
                </CommonStyles.TypographyUI>
              </div>
            ) : null}

            {/* What the package is, kept to two lines here; the package page has it in full. */}
            {specialPackage.description?.trim() ? (
              <CommonStyles.TypographyUI
                use="paragraph"
                className="text-common-subtext text-sm font-normal font-['Inter'] !mb-0 line-clamp-2"
                title={specialPackage.description.trim()}
              >
                {specialPackage.description.trim()}
              </CommonStyles.TypographyUI>
            ) : null}

            {/* The status closes the package's own details, under its name and site. */}
            <TagSpecialPackageStatus status={specialPackage.status as ESpecialPackageStatus} />

            {/* {specialPackage.start_date ? (
              <div className="flex items-center gap-2">
                <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
                  Validity:{" "}
                </CommonStyles.TypographyUI>
                <CommonStyles.TypographyUI use="paragraph" className="text-common-negative text-xs font-normal font-['Inter'] !mb-0">
                  {dayjs(specialPackage.start_date, "YYYY-MM-DD").format("DD/MM/YYYY")}
                </CommonStyles.TypographyUI>
              </div>
            ) : null} */}
          </div>
        </div>

        <div className="w-fit max-sm:w-full">
          <CommonStyles.ButtonUI
            ghost
            className="float-right"
            type="primary"
            onClick={() => navigate(baseRoutes.userPackageDetail.replace(":key", specialPackage.key ? (specialPackage.key as string) : ""))}
          >
            {t("user.profile.viewDetail")}
          </CommonStyles.ButtonUI>
        </div>
      </div>
    </div>
  );
}
